import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface FramelessInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const FramelessInsights: React.FC<FramelessInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();

  const currentMs = (globalFrame / fps) * 1000;

  // 1. Identify which bullet insight is currently active in time
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  // Fallback to ensure we are always displaying an active insight
  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];

  // 2. Transition physics
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // High-performance crisp text transitions
  const opacity = interpolate(elapsedFrames, [0, 6], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const translateX = interpolate(elapsedFrames, [0, 8], [-30, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Vertical line accent height growth animation
  const borderHeightScale = interpolate(elapsedFrames, [0, 10], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "flex-start",
        width: "100%",
        zIndex: 10,
        margin: "auto 0",
        padding: "20px 10px",
      }}
    >
      <div
        style={{
          display: "flex",
          position: "relative",
          width: "100%",
          paddingLeft: "30px", // Give space for left vertical accent line
          minHeight: "330px", // Increased to comfortably accommodate 54px dominant font
          alignItems: "center",
        }}
      >
        {/* Glowing Left Neon Accent Border Line (Grows dynamically!) */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: "5%",
            width: "6px",
            height: `${90 * borderHeightScale}%`,
            background:
              "linear-gradient(180deg, var(--accent-yellow), var(--accent-pink))",
            boxShadow: "0 0 15px rgba(255, 209, 102, 0.6)",
            borderRadius: "4px",
          }}
        />

        {/* Dynamic large left-aligned takeaway paragraph */}
        <div
          style={{
            opacity,
            transform: `translateX(${translateX}px)`,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Dot pagination + brand badge */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              {bullets.map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: i === activeIndex ? 20 : 6,
                    height: 6,
                    borderRadius: 3,
                    background:
                      i === activeIndex
                         ? "var(--accent-yellow)"
                      : "rgba(255,209,102,0.2)",
                  boxShadow:
                    i === activeIndex
                      ? "0 0 6px rgba(255,209,102,0.4)"
                      : "none",
                }}
              />
            ))}
            </div>
            {(() => {
              const brand = detectBrandInText(activeBullet.text);
              return brand ? (
                <CompanyBadge
                  name={brand}
                  color="#ffd166"
                  accentColor="#ffd166"
                  size={72}
                  style="badge"
                  opacity={opacity}
                />
              ) : null;
            })()}
          </div>

          <p
            style={{
              fontFamily: "var(--font-takeaway)",
              fontSize: "54px", // Dominant primary message size!
              fontWeight: 600,
              lineHeight: 1.4,
              color: "#f8f9fa",
              textAlign: "left",
              letterSpacing: "-0.5px",
            }}
          >
            <HighlightText
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
